import { useMemo, type PropsWithChildren } from 'react';
import SingleFetchProvider from '@atlaskit/feature-gate-single-fetch-provider';
import { FeatureGatesInitializationWithProvider } from '@atlassian/feature-gates-react';
import { getConfig } from '../../config/envConfig';
import { useToken } from '../Flow/ConsentContextProvider';
import { useAnalytics } from '../Analytics/AnalyticsProvider';

const { statsigApiKey, featureGateEnvironment, featureGatePerimeter } = getConfig();

const TARGET_APP = 'oauth-consent_web';

/**
 * Initializes Statsig feature gates for the oauth-consent app.
 *
 * Must be rendered inside ConsentContextProvider so that consent token fields
 * (userId, clientId, profile, customer) are available for Statsig targeting.
 *
 * Uses SingleFetchProvider for efficient single-request gate evaluation.
 *
 * Children are rendered regardless of initialization status — feature gates
 * default to false until initialization completes (fail-safe pattern).
 */
const FeatureGateProvider = ({ children }: PropsWithChildren) => {
  const token = useToken();
  const analytics = useAnalytics();

  const provider = useMemo(
    () => new SingleFetchProvider({ apiKey: statsigApiKey }),
    [],
  );

  const identifiers = useMemo(
    () => ({
      atlassianAccountId: token.userId,
    }),
    [token.userId],
  );

  const customAttributes = useMemo(
    () => ({
      ...(token.clientId && { clientId: token.clientId }),
      ...(token.profile && { profileName: token.profile }),
      ...(token.customer && { customerName: token.customer }),
    }),
    [token.clientId, token.customer, token.profile],
  );

  return (
    <FeatureGatesInitializationWithProvider
      enabled
      options={{
        targetApp: TARGET_APP,
        environment: featureGateEnvironment,
        perimeter: featureGatePerimeter,
        analyticsWebClient: Promise.resolve(analytics.getAnalyticsWebClient()),
      }}
      onReady={(error) => {
        if (error) {
          console.error('Could not initialise feature flags', error);
        }
      }}
      identifiers={identifiers}
      customAttributes={customAttributes}
      provider={provider}
    >
      {children}
    </FeatureGatesInitializationWithProvider>
  );
};

export default FeatureGateProvider;
